// Theming — light/dark, for the site and for the embed.
//
// The two have different inputs, because they answer to different people.
//
// THE SITE (dataseries.org itself) answers to its visitor:
//
//   1. the stored preference   auto | light | dark, from the footer control
//   2. prefers-color-scheme    what "auto" (the default) resolves to
//
// THE EMBED (`/d/<id>?embed=1`) answers to the page it is embedded in, and
// deliberately ignores the stored preference — a visitor's choice on
// dataseries.org must not leak into a chart on someone else's site:
//
//   1. `?theme=dark|light`   explicit, wins over everything, survives our own
//                            history.replaceState (see syncUrl in main.ts)
//   2. postMessage           the parent tells us, and keeps telling us on every
//                            user toggle (contract below) — needed because a
//                            host's dark mode is a runtime toggle an iframe
//                            cannot read
//   3. prefers-color-scheme  the default, and the only thing a third-party
//                            embedder who never implements (2) will hit
//
// The theme is applied as `data-theme` on <html>; all colour lives in app.css.
// The document's initial value is set by an inline script in the page <head>
// (index.html for the SPA shell, ds_page_html() for the server-rendered dataset
// page) so nothing paints in the wrong theme before this module loads. That
// script duplicates `resolveTheme` in ~5 lines — keep the two in step.

export type Theme = "light" | "dark";
/** What the visitor chose. "auto" means "whatever the OS says, whenever it says it". */
export type ThemePref = "auto" | Theme;

/** localStorage key for the site-wide preference. */
export const PREF_KEY = "ds-theme";

/** iframe -> parent, once the app is mounted and can accept a theme. */
export const READY_MESSAGE = "dataseries:ready";
/** parent -> iframe, in reply and on every subsequent user toggle. */
export const THEME_MESSAGE = "dataseries:theme";

/** A `theme=` value. Anything else — a typo, an absent param — is "unspecified". */
export function parseThemeParam(v: string | null | undefined): Theme | null {
  return v === "dark" || v === "light" ? v : null;
}

/** A stored preference. Junk in storage (or none) reads as "auto". */
export function parseThemePref(v: string | null | undefined): ThemePref {
  return v === "dark" || v === "light" || v === "auto" ? v : "auto";
}

/** What a preference means right now, given the OS setting. */
export function resolveTheme(pref: ThemePref, prefersDark: boolean): Theme {
  return pref === "auto" ? (prefersDark ? "dark" : "light") : pref;
}

/**
 * The theme carried by a `message` event's payload, or null if this is not a
 * theme message. Shape-validated but not origin-checked: this only ever changes
 * colours, and an allowlist would break local previews and any future host.
 */
export function themeFromMessage(data: unknown): Theme | null {
  if (data == null || typeof data !== "object") return null;
  const d = data as { type?: unknown; theme?: unknown };
  if (d.type !== THEME_MESSAGE) return null;
  return parseThemeParam(typeof d.theme === "string" ? d.theme : null);
}

/**
 * The explicit `theme=` from a URL. Read from both the query string and the hash
 * route (`#/d/<id>?theme=dark`), since either can carry the whole view.
 */
export function themeFromUrl(search: string, hash: string): Theme | null {
  const fromSearch = parseThemeParam(new URLSearchParams(search).get("theme"));
  if (fromSearch) return fromSearch;
  const q = hash.indexOf("?");
  if (q < 0) return null;
  return parseThemeParam(new URLSearchParams(hash.slice(q + 1)).get("theme"));
}

let current: Theme = "light";
let explicit: Theme | null = null;
let pref: ThemePref = "auto";
// One flag per entry point, not one shared: the two wire different listeners,
// and only one of them ever runs in a given page.
let siteWired = false;
let embedWired = false;
const listeners: ((t: Theme) => void)[] = [];

// localStorage throws in Safari's private mode and when cookies are blocked; a
// theme preference is never worth breaking the page over.
function readPref(): ThemePref {
  try {
    return parseThemePref(localStorage.getItem(PREF_KEY));
  } catch {
    return "auto";
  }
}
function writePref(p: ThemePref): void {
  try {
    if (p === "auto") localStorage.removeItem(PREF_KEY);
    else localStorage.setItem(PREF_KEY, p);
  } catch {
    /* preference just won't persist */
  }
}
function prefersDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

/** The theme in effect. */
export function currentTheme(): Theme {
  return current;
}

/** The explicit `?theme=` value, if the URL pinned one — else null. */
export function pinnedTheme(): Theme | null {
  return explicit;
}

/** Run `fn` after every theme change (the chart repaints its axes this way). */
export function onThemeChange(fn: (t: Theme) => void): void {
  listeners.push(fn);
}

/** Apply a theme. No-op when it is already in effect, so no needless repaints. */
export function applyTheme(t: Theme): void {
  if (t === current && document.documentElement.dataset.theme === t) return;
  current = t;
  document.documentElement.dataset.theme = t;
  for (const fn of listeners) fn(t);
}

/** The visitor's stored site preference (auto by default). */
export function themePref(): ThemePref {
  return pref;
}

/** Set and persist the site preference — what the footer control calls. */
export function setThemePref(p: ThemePref): void {
  pref = p;
  writePref(p);
  applyTheme(resolveTheme(p, prefersDark()));
}

/**
 * Site theming: apply the stored preference and keep following the OS while it
 * is "auto". Returns the theme in effect. Not used in embed mode.
 */
export function initSiteTheme(): Theme {
  pref = readPref();
  applyTheme(resolveTheme(pref, prefersDark()));
  if (!siteWired) { siteWired = true; watchOS(() => pref === "auto"); }
  return current;
}

/**
 * Embed theming: `?theme=` → parent postMessage → prefers-color-scheme, and
 * never the stored site preference. Idempotent — restoreFromUrl() also runs on
 * hashchange/popstate, and the listeners must not stack up.
 */
export function initEmbedTheme(search = location.search, hash = location.hash): Theme {
  explicit = themeFromUrl(search, hash);
  applyTheme(explicit ?? (prefersDark() ? "dark" : "light"));
  if (embedWired) return current;
  embedWired = true;
  watchOS(() => !explicit);
  window.addEventListener("message", e => {
    const t = themeFromMessage(e.data);
    if (t && !explicit) applyTheme(t);
  });
  return current;
}

// The OS setting can change while the page is open (macOS switches at sunset).
// `active` decides whether that still gets a say — it does not once the visitor
// has picked a theme, or once the URL/parent has pinned one.
function watchOS(active: () => boolean): void {
  if (typeof matchMedia !== "function") return;
  const mq = matchMedia("(prefers-color-scheme: dark)");
  if (typeof mq.addEventListener !== "function") return;
  mq.addEventListener("change", e => {
    if (active()) applyTheme(e.matches ? "dark" : "light");
  });
}

/**
 * Tell the parent we are ready to be themed. Sent to `*`: we do not know the
 * embedding origin, and the message carries nothing but the fact that a
 * dataseries embed has mounted.
 */
export function notifyEmbedReady(): void {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type: READY_MESSAGE }, "*");
  }
}
